/*
 * Copyright (c) 2024. Bluemate B.V.
 */
import './style/defaultEmailEditor.scss';

import {AwesomeIcon, ChangeFunction, Checkbox, getScreenHeight, Loader, remToPx} from '@dvrd/dvr-controls';

import React, {ChangeEventHandler, MouseEventHandler, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {EditMode, EmailFlow, UnlayerDesign} from '../../../../util/interfaces';

import DefaultEmail from '../../../../models/defaultEmailModel';

import {BluemateButton, BluemateInput} from '../../../controls';

import BluemateSwitch from '../../../controls/switch/bluemateSwitch';

import EmailEditor, {EditorRef} from 'react-email-editor';

import {getMergeTags} from '../../../../util/unlayerUtil';

import TestMailController, {TestMailRef} from '../../../email/test/testMailController';

interface Props {
    onClickBack: MouseEventHandler;
    onChangeName: ChangeFunction<string>;
    onChangeSubject: ChangeFunction<string>;
    onSelectFlow: (flow: EmailFlow) => ChangeFunction<boolean>;
    onChangeStatus: ChangeFunction<boolean>;
    onSubmit: (design: UnlayerDesign) => void;
    onChoosePreview: ChangeEventHandler<HTMLInputElement>;
    onDeletePreview: MouseEventHandler;
    onOpenPreview: MouseEventHandler;
    onClickDelete: MouseEventHandler;
    onTestEmail: (content: string, email: string) => void;
    onPreviewError: VoidFunction;
    defaultEmail: DefaultEmail;
    loading: boolean;
    previewFile: File | null;
    dataMode: EditMode;
}

export default function DefaultEmailEditor(props: Props) {
    const {t} = useTranslation();
    const {
        onClickBack,
        onChangeName,
        onSelectFlow,
        onChangeStatus,
        onSubmit,
        onChoosePreview,
        onDeletePreview,
        defaultEmail,
        loading,
        previewFile,
        onChangeSubject,
        onClickDelete,
        dataMode,
        onOpenPreview,
        onTestEmail,
        onPreviewError
    } = props;
    const [editorLoading, setEditorLoading] = useState(true);
    const [showPreview, setShowPreview] = useState(false);
    const toggleLabel = useMemo(() => {
        return defaultEmail.enabled
            ? t('admin.defaultEmails.editor.enabled', 'Ingeschakeld')
            : t('admin.defaultEmails.editor.disabled', 'Uitgeschakeld');
    }, [defaultEmail.enabled, t]);
    const previewInputRef = useRef<HTMLInputElement>(null);
    const editorRef = useRef<EditorRef>(null);
    const testMailRef = useRef<TestMailRef>(null);
    const htmlSet = useRef<boolean>(false);

    function onTogglePreview(evt: React.MouseEvent) {
        evt.stopPropagation();
        setShowPreview(!showPreview);
    }

    function onEditorLoaded() {
        setEditorLoading(false);
    }

    function onClickPreview() {
        previewInputRef.current?.click();
    }

    function onClickTest() {
        testMailRef.current?.activate();
    }

    function _onTest(email: string) {
        editorRef.current?.editor?.exportHtml((data: {html: string}) => {
            onTestEmail(data.html, email);
        });
    }

    function _onSubmit() {
        editorRef.current?.editor?.exportHtml((data: {design: UnlayerDesign}) => {
            onSubmit(data.design);
        });
    }

    function setDesign() {
        if (htmlSet.current || !editorRef.current) return;
        const design = defaultEmail.jsonTemplate;
        editorRef.current?.editor?.loadDesign(design);
        editorRef.current?.editor?.setMergeTags(getMergeTags(null));
        if (Object.keys(design).length) htmlSet.current = true;
    }

    function renderTopBar() {
        return (
            <div className='top-bar'>
                <div className='col'>
                    <div className='btn-back' onClick={onClickBack}>
                        <AwesomeIcon name='arrow-left' className='back-icon' />
                    </div>
                    <div className='inputs-col'>
                        <BluemateInput
                            onChange={onChangeName}
                            value={defaultEmail.templateName}
                            label={t('admin.defaultEmails.editor.nameLabel', 'Template naam')}
                            placeholder={t('admin.defaultEmails.editor.namePlaceholder', 'Naam van de template')}
                        />
                        <BluemateInput
                            onChange={onChangeSubject}
                            value={defaultEmail.emailSubject}
                            label={t('admin.defaultEmails.editor.subjectLabel', 'Onderwerp van mail')}
                            placeholder={t(
                                'admin.defaultEmails.editor.subjectPlaceholder',
                                'Standaard onderwerp van de e-mail'
                            )}
                        />
                    </div>
                    <div className='options-container'>
                        <div className='options-row'>
                            <div className='flow-container'>
                                <label className='flow-title'>{t('admin.defaultEmails.editor.flows', 'Flows')}</label>
                                <Checkbox
                                    checked={defaultEmail.emailFlows.includes(EmailFlow.INVOICE)}
                                    onCheck={onSelectFlow(EmailFlow.INVOICE)}
                                    label={t('admin.defaultEmails.editor.flow.invoice', 'Factuur')}
                                />
                                <Checkbox
                                    checked={defaultEmail.emailFlows.includes(EmailFlow.REVIEW_INVOICE)}
                                    onCheck={onSelectFlow(EmailFlow.REVIEW_INVOICE)}
                                    label={t('admin.defaultEmails.editor.flow.reviewInvoice', 'Review + Factuur')}
                                />
                                <Checkbox
                                    checked={defaultEmail.emailFlows.includes(EmailFlow.CREDIT_INVOICE)}
                                    onCheck={onSelectFlow(EmailFlow.CREDIT_INVOICE)}
                                    label={t('admin.defaultEmails.editor.flow.creditInvoice', 'Creditfactuur')}
                                />
                            </div>
                            <BluemateSwitch
                                value={defaultEmail.enabled}
                                onChange={onChangeStatus}
                                mainLabel={t('admin.defaultEmails.editor.statusLabel', 'Status')}
                                labels={['', toggleLabel]}
                                activeGreen
                            />
                        </div>
                        <div className='options-row'>
                            <BluemateButton
                                onClick={onClickTest}
                                label={t('admin.defaultEmails.editor.sendTest', 'Test verzenden')}
                                ornaments={{element: <AwesomeIcon name='paper-plane' />}}
                                secondary
                            />
                            {dataMode === EditMode.EDIT && (
                                <BluemateButton
                                    onClick={onClickDelete}
                                    label={t('admin.defaultEmails.editor.deleteTemplate', 'Template verwijderen')}
                                    ornaments={{element: <AwesomeIcon name='trash-alt' />}}
                                    disabled={defaultEmail.enabled}
                                    color={'red'}
                                    secondary
                                />
                            )}
                        </div>
                    </div>
                </div>
                {renderPreview()}
                <BluemateButton onClick={_onSubmit} label={t('admin.defaultEmails.editor.save', 'Opslaan')} />
            </div>
        );
    }

    function renderPreview() {
        if (!showPreview)
            return (
                <BluemateButton
                    onClick={onTogglePreview}
                    label={t('admin.defaultEmails.editor.previewOptions', 'Preview opties')}
                    ornaments={{element: <AwesomeIcon name='cog' />}}
                    secondary
                />
            );
        let previewError: boolean = false;
        let imageURL: string | null;
        if (previewFile) imageURL = URL.createObjectURL(previewFile);
        else {
            previewError = defaultEmail.previewURLError;
            imageURL = defaultEmail.previewURL;
        }
        return (
            <div className='preview-container' onClick={onClickPreview}>
                <input ref={previewInputRef} type='file' style={{display: 'none'}} onChange={onChoosePreview} />
                {!!imageURL && !previewError ? (
                    <img src={imageURL} alt='' className='preview-img' onError={onPreviewError} />
                ) : (
                    <AwesomeIcon name='image' className='preview-placeholder-icon' />
                )}
                <div className='preview-actions'>
                    <AwesomeIcon
                        name='times'
                        onClick={onTogglePreview}
                        className='preview-action hide'
                        title={t('admin.defaultEmails.editor.hide', 'Verbergen')}
                    />
                    <div className='right'>
                        {!!imageURL && (
                            <>
                                <AwesomeIcon name='search' onClick={onOpenPreview} className='preview-action view' />
                                <AwesomeIcon
                                    name='trash-alt'
                                    onClick={onDeletePreview}
                                    className='preview-action delete'
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (defaultEmail.jsonTemplate && !editorLoading) setDesign();
    }, [defaultEmail, editorLoading]);

    const screenHeight = getScreenHeight();
    const headerHeight = document.querySelector('div.bluemate-header')?.getBoundingClientRect().height ?? 0;
    const editorHeight = screenHeight - headerHeight - remToPx(19);
    return (
        <>
            <div className='default-template-editor'>
                <Loader active={loading} />
                {renderTopBar()}
                <div className='html-editor-container'>
                    <div style={editorLoading ? {display: 'none'} : undefined}>
                        <EmailEditor
                            ref={editorRef}
                            onLoad={onEditorLoaded}
                            projectId={43580}
                            minHeight={editorHeight}
                            options={{
                                features: {
                                    smartMergeTags: false
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <TestMailController onSubmit={_onTest} ref={testMailRef} closeOnSubmit />
        </>
    );
}
